/* # Constants & Helpers 
1. Table Support - Added TABLE, TR, TD, TH, THEAD, TBODY to allowedTags
2. Favicon Safety - Switched to data-uri in index.html
3. HTML Utilities - Expanded for rich table rendering
*/

export const STORAGE_KEY = 'snippet_manager_snippets';
export const PINNED_KEY = 'snippet_manager_pinned_ids';
export const USAGE_KEY = 'snippet_manager_usage_counts';
export const CONFIG_KEY = 'snippet_boss_config';
export const APPS_SCRIPT_URL_KEY = 'snippet_manager_apps_script_url';
export const DIRTY_IDS_KEY = 'snippet_manager_dirty_ids';
export const LAST_SYNC_AT_KEY = 'snippet_manager_last_sync_at';
export const TOKEN_VALUES_KEY = 'snippet_manager_token_values';
export const MAX_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
export const GOOGLE_SHEETS_CELL_LIMIT = 50000;
export const CLOUD_SYNC_WARNING_CHARS = 40000;
export const CLOUD_SYNC_DANGER_CHARS = 46000;
export const CLOUD_SYNC_SAFE_LIMIT = 48000;

export const VITE_APPS_SCRIPT_URL = "";
export const WORKSPACE_ID = "default";

export const generateId = () => crypto.randomUUID();

export function hashText_(s) {
  if (!s) return "";
  let hash = 0;
  for (let i = 0; i < s.length; i++) {
    const char = s.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return hash.toString();
}

export function titleFromText_(text) {
  if (!text) return "Clipboard snippet";
  const firstLine = text.trim().split('\n')[0].trim();
  if (!firstLine) return "Clipboard snippet";
  return firstLine.substring(0, 40) + (firstLine.length > 40 ? "..." : "");
}

export const sanitizeHtml = (html) => {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  
  // Expanded tags to support TABLES
  const allowedTags = [
    'A', 'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 
    'UL', 'OL', 'LI', 'PRE', 'CODE', 'SPAN', 'DIV',
    'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD'
  ];
  
  const clean = (node) => {
    const children = Array.from(node.childNodes);
    children.forEach(child => {
      if (child.nodeType === 1) {
        if (!allowedTags.includes(child.tagName)) {
          const text = document.createTextNode(child.textContent);
          child.parentNode.replaceChild(text, child);
        } else {
          const attrs = Array.from(child.attributes);
          attrs.forEach(attr => {
            const name = attr.name.toLowerCase();
            // Allow href but sanitize
            if (name === 'href' && child.tagName === 'A') {
              if (attr.value.toLowerCase().startsWith('javascript:')) {
                child.removeAttribute(attr.name);
              }
            } else {
              child.removeAttribute(attr.name);
            }
          });
          clean(child);
        }
      }
    });
  };
  clean(doc.body);
  return doc.body.innerHTML;
};

export const htmlToPlainText = (html) => {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const processNode = (node) => {
    let text = '';
    node.childNodes.forEach(child => {
      if (child.nodeType === 3) text += child.textContent;
      else if (child.nodeType === 1) {
        const tag = child.tagName;
        if (tag === 'BR') text += '\n';
        else if (tag === 'P' || tag === 'DIV' || tag === 'TR') text += '\n' + processNode(child) + '\n';
        else if (tag === 'TD' || tag === 'TH') text += ' | ' + processNode(child);
        else if (tag === 'LI') text += '\n- ' + processNode(child);
        else text += processNode(child);
      }
    });
    return text;
  };
  return processNode(doc.body).replace(/\n\s*\n\s*\n/g, '\n\n').trim();
};

export const escapeToHtml = (text) => {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\n/g, '<br>');
};

export const SYSTEM_TOKEN_NAMES = new Set([
  'today', 'date', 'now', 'time', 'weekday', 'month', 'year',
  'timestamp', 'iso_date', 'iso_datetime'
]);

export function formatDateDDMMMYYYY(dateObj = new Date()) {
  const day = String(dateObj.getDate()).padStart(2, '0');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${day}-${months[dateObj.getMonth()]}-${dateObj.getFullYear()}`;
}

export function getSystemTokenMap(nowDate = new Date()) {
  const formattedDate = formatDateDDMMMYYYY(nowDate);
  const hours = String(nowDate.getHours()).padStart(2, '0');
  const minutes = String(nowDate.getMinutes()).padStart(2, '0');
  const time24 = `${hours}:${minutes}`;
  return {
    today: formattedDate,
    date: formattedDate,
    iso_date: formattedDate,
    time: time24,
    now: `${formattedDate} ${time24}`,
    iso_datetime: `${formattedDate} ${time24}`,
    weekday: nowDate.toLocaleDateString(undefined, { weekday: 'long' }),
    month: nowDate.toLocaleDateString(undefined, { month: 'long' }),
    year: String(nowDate.getFullYear()),
    timestamp: String(nowDate.getTime())
  };
}

export function replaceSystemTokens(content, nowDate = new Date()) {
  if (!content) return '';
  const tokens = getSystemTokenMap(nowDate);
  return Object.entries(tokens).reduce((result, [key, value]) => {
    const regex = new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'gi');
    return result.replace(regex, value);
  }, content);
}

export function extractNonSystemTokens(content) {
  if (!content) return [];
  const matches = [...content.matchAll(/{{\\s*([^}]+?)\\s*}}/g)]
    .map(match => match[1].trim())
    .filter(Boolean)
    .filter(token => !SYSTEM_TOKEN_NAMES.has(token.toLowerCase()));
  return [...new Set(matches)];
}

export function htmlToMarkdown(html) {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const render = (node) => {
    if (node.nodeType === 3) return node.textContent || '';
    if (node.nodeType !== 1) return '';

    const inner = Array.from(node.childNodes).map(render).join('');
    switch (node.tagName) {
      case 'BR': return '\n';
      case 'P':
      case 'DIV': return inner.trim() ? inner.trim() + '\n\n' : '';
      case 'STRONG':
      case 'B': return inner ? `**${inner}**` : '';
      case 'EM':
      case 'I': return inner ? `*${inner}*` : '';
      case 'CODE': return `\`${inner}\``;
      case 'PRE': return `\`\`\`\n${node.textContent || ''}\n\`\`\`\n\n`;
      case 'LI': return `- ${inner.trim()}\n`;
      case 'A': {
        const href = node.getAttribute('href');
        return href ? `[${inner || href}](${href})` : inner;
      }
      case 'TR': return `${inner.trim()}\n`;
      case 'TH':
      case 'TD': return `| ${inner.trim()} `;
      default: return inner;
    }
  };

  return render(doc.body)
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

export function snippetFingerprint(snippets = []) {
  return hashText_(
    snippets
      .map(s => `${s.id}:${Number(s.updatedAt || 0)}:${s.isDeleted ? 1 : 0}`)
      .sort()
      .join('|')
  );
}